import { Children, ReactNode } from "react";
import { filter, get } from "lodash";
import qs from "qs";

import * as routes from "routes";
import { NotificationMessage } from "services/app/types";
import { InvestmentTerm } from "services/listings/types";
import { EarningByRegion } from "services/valuation/types";

import { VP_URL } from "../constants";

export function capitalize(str: string) {
  return str.replace(/(?:^|\s)\S/g, function (a: string) {
    return a.toUpperCase();
  });
}

export function getCookie(name: string) {
  const value = "; " + document.cookie;
  const parts = value.split("; " + name + "=");
  if (parts.length === 2) {
    return parts.pop().split(";").shift();
  }
}

// tslint:disable-next-line:typedef
export function formatMoney(
  amount: string | number,
  decimalCount = 2,
  decimal = ".",
  thousands = ",",
) {
  decimalCount = isNaN((decimalCount = Math.abs(decimalCount)))
    ? 2
    : decimalCount;
  decimal = decimal === undefined ? "." : decimal;
  thousands = thousands === undefined ? "," : thousands;
  const s = amount < 0 ? "-" : "";
  // tslint:disable-next-line:radix
  const i = String(
    parseInt((amount = Math.abs(Number(amount) || 0).toFixed(decimalCount))),
  );
  const j: number = i.length > 3 ? i.length % 3 : 0;

  return (
    s +
    (j ? i.substr(0, j) + thousands : "") +
    i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousands) +
    (decimalCount
      ? decimal +
        Math.abs(Number.parseFloat(amount) - Number.parseFloat(i))
          .toFixed(decimalCount)
          .slice(2)
      : "")
  );
}

// Source: https://stackoverflow.com/a/46181
export function validateEmail(email: string) {
  // tslint:disable-next-line:max-line-length
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

export function validatePassword(password: string) {
  // regex requires at least:
  // 1 lower case character
  // 1 uppercase character
  // 1 digit or special character
  const re = /((?=.*[a-z])(?=.*[A-Z])(?=.*[\W\d]).*)/;
  return re.test(password) && password.length >= 8;
}

export function validateCountryCode(code: string) {
  const re = /^[A-Z]{2}$/;
  return re.test(code);
}

export function keepNumbers(value: string) {
  return value.replace(/[^0-9]/gi, "");
}

export function range(n: number): number[] {
  return Array(n)
    .fill(1)
    .map((_, i) => i);
}

function getAuctionPrefix(currentUrl?: URL): string {
  // TODO fixing different urls pro production and staging seems a good case of use of the .env file
  currentUrl = currentUrl || new URL(window.location.toLocaleString());
  return currentUrl.origin.replace("worth", "auctions");
}

export function navigateTo(
  uri: string,
  cleanUp?: () => void,
  openOnNewTab?: boolean,
) {
  if (cleanUp) {
    cleanUp();
  }

  if (openOnNewTab) {
    window.open(uri, "_blank");
  } else {
    window.location.href = uri;
  }
}

export function goToOrderbookBuyerDashboard(
  cleanUp?: () => void,
  openOnNewTab?: boolean,
) {
  const prefixUrl = getAuctionPrefix();
  const url = `${prefixUrl}${routes.orderBookBuyerDashboard()}`;
  navigateTo(url, cleanUp, openOnNewTab);
}

export function goToVP(
  cleanUp?: () => void,
  openOnNewTab?: boolean,
  path?: string,
) {
  const url = path ? `${VP_URL}${path}` : VP_URL;
  navigateTo(url, cleanUp, openOnNewTab);
}

export function getDealPageUrl(dealOfferId: number | string) {
  const prefixUrl = getAuctionPrefix();
  return `${prefixUrl}/orderbook/deal-page?offerId=${dealOfferId}`;
}

export function goToListingDealPage(
  dealOfferId: number | string,
  cleanUp?: () => void,
  openOnNewTab?: boolean,
) {
  const url = getDealPageUrl(dealOfferId);
  navigateTo(url, cleanUp, openOnNewTab);
}

export function valueIsDefined<T>(v: T): boolean {
  return v !== null && v !== undefined;
}

export function goToAuctions404(cleanUp?: () => void) {
  navigateTo("/404/", cleanUp);
}

// staging and production database will only take latin1 characters
// for the list of latin1 characters, check https://www.utf8-chartable.de/unicode-utf8-table.pl
// eslint-disable-next-line
const invalidChars = /[^\x00-\xFF]/;
const characterIsInvalid = (c: string) => invalidChars.test(c);

export function stringIsValid(s: string) {
  const hasInvalidCharacter = s.split("").some(characterIsInvalid);
  return !hasInvalidCharacter;
}

export function listInvalidCharacters(s: string) {
  return Array.from(new Set(s.split("").filter(characterIsInvalid)));
}

export function singularOrPlural(
  singular: string,
  plural: string,
  value: number,
) {
  return value > 1 ? plural : singular;
}

export function arrayJoinWithGetter<T, S>(
  a: T[],
  b: (d: number) => S,
): (T | S)[] {
  if (a.length === 0) {
    return [] as (T | S)[];
  }
  const out = [] as (T | S)[];
  for (let i = 0; i < a.length - 1; i++) {
    out.push(a[i]);
    out.push(b(i));
  }
  out.push(a[a.length - 1]);
  return out;
}

export function appendToClassName(tail: string, className?: string) {
  const names = [];
  if (valueIsDefined(tail)) {
    names.push(tail);
  }

  if (valueIsDefined(className)) {
    names.push(className);
  }

  return names.join(" ");
}

export function estimateStringLengthInPixels(s: string) {
  const commaCount = (s.match(/,/g) || []).length;
  const dotCount = (s.match(/./g) || []).length;
  const numberCount = (s.match(/[0-9]/g) || []).length;
  return numberCount * 9 + commaCount * 5 + dotCount * 2;
}

export function childrenAsArray<T>(children: ReactNode): T[] {
  return Children.toArray(children).map(x => (x as undefined) as T);
}

function roundMinimal(value: number) {
  if (value % 1 !== 0) {
    return value.toFixed(value < 10 ? 1 : 0);
  } else {
    return value.toFixed(0);
  }
}

export function formatBigNumber(value: number) {
  if (Math.abs(value) < 1000) {
    return roundMinimal(value);
  }
  value = value / 1000;
  if (Math.abs(value) < 1000) {
    return roundMinimal(value) + "K";
  }
  value = value / 1000;
  if (Math.abs(value) < 1000) {
    return roundMinimal(value) + "M";
  }
  value = value / 1000;
  return roundMinimal(value) + "G";
}

export function extractLazy<T>(val: T | (() => T)) {
  const fun = val as () => T;
  if (valueIsDefined(fun.call)) {
    return fun();
  }
  return val as T;
}

export function getTermNiceName(term: InvestmentTerm) {
  const termNameToDisplay: { [K in InvestmentTerm]: string } = {
    life_of_rights: "Life of Rights",
    "30_year": "30-Year",
    partial_30_year: "30-Year",
    "10_year": "10-Year",
    partial_10_year: "10-Year",
    fixed_return: "Fixed-Return",
  };
  return termNameToDisplay[term];
}

export function getTermDisplayName(term: InvestmentTerm, short?: boolean) {
  const termNameToDisplayAssetDetail: { [K in InvestmentTerm]: string } = {
    life_of_rights: "Life of Rights",
    "30_year": "Term Advance 30-Year",
    partial_30_year: "Term Advance 30-Year",
    "10_year": "Term Advance 10-Year",
    partial_10_year: "Term Advance 10-Year",
    fixed_return: "Fixed-Return",
  };
  const termNameToDisplayAssetDetailShort: { [K in InvestmentTerm]: string } = {
    life_of_rights: "Life of Rights",
    "30_year": "Term Adv 30-Year",
    partial_30_year: "Term Adv 30-Year",
    "10_year": "Term Adv 10-Year",
    partial_10_year: "Term Adv 10-Year",
    fixed_return: "Fixed-Return",
  };
  return short
    ? termNameToDisplayAssetDetailShort[term]
    : termNameToDisplayAssetDetail[term];
}

export function getBaseURL() {
  return navigator.userAgent === "ReactSnap"
    ? import.meta.env.REACT_APP_REACT_SNAP_BASE_SERVER_URL
    : import.meta.env.REACT_APP_BASE_SERVER_URL || "";
}

export function trackViewEvents() {
  return (
    import.meta.env.PROD &&
    navigator.userAgent !== "ReactSnap" &&
    !import.meta.env.REACT_APP_IS_BUILD_CI
  );
}

export function getQueryParams() {
  return qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  }) as { [key: string]: undefined | string };
}

export function mountQueryString(obj: any) {
  return qs.stringify(obj, { addQueryPrefix: true });
}

export function filterWarnings(notificationMessages: NotificationMessage[]) {
  return filter(
    notificationMessages,
    notification => notification.tag !== "warning",
  );
}

export const getEarningAmount = (earning: number | EarningByRegion): number =>
  typeof earning === "number"
    ? earning
    : get(earning, "total", get(earning, "all"));
